import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useSocket } from './SocketContext';
import api from '../utils/api';

const BusinessContext = createContext();

// Export BusinessContext for components that need to use it conditionally
export { BusinessContext };

export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
};

export const BusinessProvider = ({ children }) => {
  const { user } = useAuth();
  const { socket, isConnected } = useSocket();
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasNoBusiness, setHasNoBusiness] = useState(false);

  // Check if user is authenticated and is a supplier
  const isAuthenticated = user && user.role === 'supplier';

  // Load businesses when user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Add a small delay to ensure auth is fully loaded
      const timer = setTimeout(() => {
        fetchBusinesses();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  // Listen for real-time business updates via socket
  useEffect(() => {
    if (!socket || !isConnected || !isAuthenticated) return;

    const handleBusinessUpdate = (data) => {
      if (data.business) {
        // Update businesses list
        setBusinesses(prev => 
          prev.map(b => 
            b.business._id === data.business._id 
              ? { ...b, business: { ...b.business, ...data.business } }
              : b
          )
        );

        // Update selected business if it's the one that changed
        setSelectedBusiness(prev => {
          if (prev && prev.business._id === data.business._id) {
            return { ...prev, business: { ...prev.business, ...data.business } };
          }
          return prev;
        });
      }
    };

    socket.on('business-updated', handleBusinessUpdate);

    return () => {
      socket.off('business-updated', handleBusinessUpdate);
    };
  }, [socket, isConnected, isAuthenticated]);

  // Ensure free trial is started on first supplier login (idempotent)
  useEffect(() => {
    const businessId = selectedBusiness?.business?._id;
    if (!isAuthenticated || !businessId) return;

    api.post(`/api/business/${businessId}/subscriptions/ensure-trial`)
      .catch(() => { /* Silent - trial start is best-effort */ });
  }, [isAuthenticated, selectedBusiness?.business?._id]);

  // Set selected business from localStorage on mount
  useEffect(() => {
    const savedBusinessId = localStorage.getItem('currentBusinessId');
    if (!savedBusinessId || businesses.length === 0) {
      if (businesses.length > 0) {
        setSelectedBusiness(businesses[0]);
        localStorage.setItem('currentBusinessId', String(businesses[0].business._id));
      }
      return;
    }
    const business = businesses.find(b => String(b.business._id) === savedBusinessId);
    if (business) {
      setSelectedBusiness(business);
    } else {
      localStorage.removeItem('currentBusinessId');
      setSelectedBusiness(businesses[0]);
      localStorage.setItem('currentBusinessId', String(businesses[0].business._id));
    }
  }, [businesses]);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      setError('');
      setHasNoBusiness(false);
      
      const response = await api.get('/api/supplier/my-profile');
      
      if (response.data.businesses && response.data.businesses.length > 0) {
        setBusinesses(response.data.businesses);
        setHasNoBusiness(false);
      } else {
        setBusinesses([]);
        setHasNoBusiness(true);
        setError('No businesses found. Please register a business first.');
      }
    } catch (error) {
      setError('Failed to load businesses: ' + (error.response?.data?.message || error.message));
      setHasNoBusiness(true);
    } finally {
      setLoading(false);
    }
  };

  const selectBusiness = (businessId) => {
    const id = businessId != null ? String(businessId) : null;
    const business = businesses.find(b => String(b.business._id) === id);
    if (business) {
      setSelectedBusiness(business);
      localStorage.setItem('currentBusinessId', id);
    }
  };

  const refreshBusinesses = async () => {
    await fetchBusinesses();
  };

  const hasPermission = (permission) => {
    if (!selectedBusiness) return false;
    if (selectedBusiness.role === 'owner' || selectedBusiness.role === 'admin') return true;
    return selectedBusiness.permissions.includes(permission);
  };

  const hasRole = (role) => {
    if (!selectedBusiness) return false;
    return selectedBusiness.role === role;
  };

  const isOwner = () => hasRole('owner');
  const isManager = () => hasRole('manager') || hasRole('owner');
  const isStaff = () => hasRole('staff') || hasRole('manager') || hasRole('owner');

  const value = {
    businesses,
    selectedBusiness,
    loading,
    error,
    hasNoBusiness,
    selectBusiness,
    refreshBusinesses,
    hasPermission,
    hasRole,
    isOwner,
    isManager,
    isStaff,
    businessId: selectedBusiness?.business?._id,
    businessName: selectedBusiness?.business?.name,
    businessCategory: selectedBusiness?.business?.category,
    userRole: selectedBusiness?.role,
    userPermissions: selectedBusiness?.permissions || []
  };

  // Debug logging for the value object (commented out for production)
  // console.log('BusinessContext: Value object - selectedBusiness:', selectedBusiness);
  // console.log('BusinessContext: Value object - businessName:', value.businessName);
  // console.log('BusinessContext: Value object - userRole:', value.userRole);
  // console.log('BusinessContext: Value object - businessId:', value.businessId);
  // console.log('BusinessContext: Value object - selectedBusiness.business:', selectedBusiness?.business);

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
};
